import { CommonRegistry, FacingDirection, WeaponData } from '@common/components/components';
import { ItemRegistry } from '@common/data/ItemRegistry';
import { World } from '@common/World';

import { EquipmentService } from './EquipmentService';

export const BARE_HANDS_STATS: WeaponData = {
  damage: 5,
  range: 16,
  arc: 0,
  cooldownMs: 300,
  knockbackForce: 50,
  swingPattern: 'stab',
  hitboxCount: 2,
  bladeWidth: 8,
};

/**
 * Global constant for the visual duration of a weapon swing animation.
 * Must be consistent between server (hit detection) and client (rendering).
 */
export const DEFAULT_SWING_DURATION_MS = 200;

export class CombatService {
  /**
   * Checks if two axis-aligned rectangles overlap.
   */
  public rectsOverlap(
    x1: number,
    y1: number,
    w1: number,
    h1: number,
    x2: number,
    y2: number,
    w2: number,
    h2: number,
  ): boolean {
    return x1 < x2 + w2 && x1 + w1 > x2 && y1 < y2 + h2 && y1 + h1 > y2;
  }

  /**
   * Calculates the position for a hitbox along an arc swing.
   *
   * @param progress 0.0 to 1.0 swing progress
   * @param hitboxIndex Which hitbox (0 = base, hitboxCount-1 = tip)
   * @param hitboxCount Total hitboxes
   * @param maxExtension Weapon range (tip distance)
   * @param arcDegrees Total arc sweep (e.g., 90)
   * @param direction Facing direction of attacker
   * @returns Local {x, y} offset from attacker's center
   */
  public getArcSwingOffset(
    progress: number,
    hitboxIndex: number,
    hitboxCount: number,
    maxExtension: number,
    arcDegrees: number,
    direction: FacingDirection,
  ): { x: number; y: number } {
    // 0. Validate inputs to prevent division by zero or out-of-bounds math
    const safeCount = Math.max(1, hitboxCount);
    const safeIndex = Math.min(Math.max(0, hitboxIndex), safeCount - 1);
    /**
     * Map cardinal directions to base angles (radians).
     * Note: 0 is right, Math.PI/2 is down (screen Y is positive down).
     */
    let baseAngle: number;
    switch (direction) {
      case 'right':
        baseAngle = 0;
        break;
      case 'down':
        baseAngle = Math.PI / 2;
        break;
      case 'left':
        baseAngle = Math.PI;
        break;
      case 'up':
        baseAngle = -Math.PI / 2;
        break;
      default:
        baseAngle = 0;
    }

    // 2. Handle arc degrees or explicit stab pattern
    if (arcDegrees === 0) {
      // Stab motion: straight out and back
      const extensionFactor = 1 - Math.abs(progress - 0.5) * 2;
      const radius = maxExtension * ((safeIndex + 1) / safeCount) * extensionFactor;
      return {
        x: Math.cos(baseAngle) * radius,
        y: Math.sin(baseAngle) * radius,
      };
    }

    // 3. Arc swing motion
    const arcRadians = (arcDegrees * Math.PI) / 180;

    /**
     * Sweep from (baseAngle - halfArc) to (baseAngle + halfArc).
     * progress 0.0 is the start of the sweep, 1.0 is the end.
     */
    const currentAngle = baseAngle - arcRadians / 2 + arcRadians * progress;

    /**
     * Each hitbox is positioned at a fixed radius based on its index.
     * Index 0 is closest to the attacker, index (hitboxCount-1) is at max range.
     */
    const radius = maxExtension * ((safeIndex + 1) / safeCount);

    return {
      x: Math.cos(currentAngle) * radius,
      y: Math.sin(currentAngle) * radius,
    };
  }

  /**
   * @deprecated Use WeaponSwing systems for hit detection instead.
   * Checks 9 points on the target's hitbox (corners, midpoints, and center).
   * This multi-point sampling ensures that attacks reliably connect even when
   * arcs are narrow or target hitboxes are large, preventing "holes" in detection.
   * If any point is within the circular sector (range + angle), it's a hit.
   *
   * @param attackerX Attacker X position
   * @param attackerY Attacker Y position
   * @param facing Attacker facing direction
   * @param range Maximum range of the attack
   * @param arcDegrees Full width of the attack arc in degrees
   * @param targetX Target X position (transform)
   * @param targetY Target Y position (transform)
   * @param width Target hitbox width
   * @param height Target hitbox height
   * @param offsetX Target hitbox offset X from transform
   * @param offsetY Target hitbox offset Y from transform
   */
  public isInAttackArc(
    attackerX: number,
    attackerY: number,
    facing: FacingDirection,
    range: number,
    arcDegrees: number,
    targetX: number,
    targetY: number,
    width: number = 0,
    height: number = 0,
    offsetX: number = 0,
    offsetY: number = 0,
  ): boolean {
    // Collect 9 sample points from the hitbox (top-left origin)
    const left = targetX + offsetX;
    const right = targetX + offsetX + width;
    const top = targetY + offsetY;
    const bottom = targetY + offsetY + height;
    const centerX = targetX + offsetX + width / 2;
    const centerY = targetY + offsetY + height / 2;

    const points = [
      { x: left, y: top }, // Top-left
      { x: centerX, y: top }, // Top-center
      { x: right, y: top }, // Top-right
      { x: left, y: centerY }, // Middle-left
      { x: centerX, y: centerY }, // Center
      { x: right, y: centerY }, // Middle-right
      { x: left, y: bottom }, // Bottom-left
      { x: centerX, y: bottom }, // Bottom-center
      { x: right, y: bottom }, // Bottom-right
    ];

    // If any point is in the arc, it's a hit
    for (const point of points) {
      if (this.isPointInArc(attackerX, attackerY, facing, range, arcDegrees, point.x, point.y)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Helper to check if a single point is within a circular sector.
   */
  private isPointInArc(
    attackerX: number,
    attackerY: number,
    facing: FacingDirection,
    range: number,
    arcDegrees: number,
    pointX: number,
    pointY: number,
  ): boolean {
    const dx = pointX - attackerX;
    const dy = pointY - attackerY;
    const distanceSq = dx * dx + dy * dy;

    // Check range first
    if (distanceSq > range * range) {
      return false;
    }

    // Special case for zero distance (e.g. overlapping hitboxes)
    if (distanceSq === 0) {
      return true;
    }

    // Calculate angle to target in degrees
    const angleToTarget = (Math.atan2(dy, dx) * 180) / Math.PI;

    // Get base angle from facing
    let baseAngle: number;
    switch (facing) {
      case 'right':
        baseAngle = 0;
        break;
      case 'down':
        baseAngle = 90;
        break;
      case 'left':
        baseAngle = 180;
        break;
      case 'up':
        baseAngle = -90;
        break;
      default:
        baseAngle = 0;
    }

    // Normalize angle difference to (-180, 180]
    let angleDiff = angleToTarget - baseAngle;
    while (angleDiff > 180) angleDiff -= 360;
    while (angleDiff < -180) angleDiff += 360;

    // Check if within arc
    return Math.abs(angleDiff) <= arcDegrees / 2;
  }

  /**
   * Calculates a knockback vector for a target hit by an attacker.
   *
   * @param attackerX Attacker X position
   * @param attackerY Attacker Y position
   * @param targetX Target X position
   * @param targetY Target Y position
   * @param force Force of the knockback
   */
  public calculateKnockback(
    attackerX: number,
    attackerY: number,
    targetX: number,
    targetY: number,
    force: number,
  ): { x: number; y: number } {
    const dx = targetX - attackerX;
    const dy = targetY - attackerY;

    const length = Math.sqrt(dx * dx + dy * dy);

    if (length === 0) {
      // If perfectly overlapping, push in a default direction (down)
      return { x: 0, y: force };
    }

    // Normalize and scale by force
    return {
      x: (dx / length) * force,
      y: (dy / length) * force,
    };
  }

  /**
   * Gets the weapon stats for an entity based on its equipped item in mainHand.
   * Logic is shared between server execution and client visualization.
   *
   * @param world World instance
   * @param equipmentService Equipment service instance
   * @param entityId Entity to check
   */
  public getWeaponStats<R extends CommonRegistry>(
    world: World<R>,
    equipmentService: EquipmentService,
    entityId: number,
  ): WeaponData {
    const mainHandItem = equipmentService.getEquipped(entityId, 'mainHand');

    if (mainHandItem) {
      // Check if item entity has a Weapon component first (for unique stats)
      const weaponComponent = world.getComponent(mainHandItem, 'Weapon');
      if (weaponComponent) {
        return weaponComponent;
      }

      // Fallback to base ItemRegistry stats
      const itemData = world.getComponent(mainHandItem, 'Item');
      if (itemData) {
        const itemDef = ItemRegistry[itemData.itemId];
        if (itemDef && itemDef.weaponStats) {
          return itemDef.weaponStats;
        }
      }
    }

    // Default "Bare Hands" stats
    return BARE_HANDS_STATS;
  }

  /**
   * Calculates the center point of a hitbox relative to the entity's transform.
   *
   * @param hitbox Hitbox component data
   * @returns Local {x, y} center of the hitbox
   */
  public getHitboxCenter(hitbox: {
    offsetX: number;
    offsetY: number;
    width: number;
    height: number;
  }): { x: number; y: number } {
    return {
      x: hitbox.offsetX + hitbox.width / 2,
      y: hitbox.offsetY + hitbox.height / 2,
    };
  }
}
